import React from 'react';
import { Timeline as AntTimeline, TimelineProps as AntTimelineProps, TimelineItemProps as AntTimelineItemProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface TimelineProps extends AntTimelineProps {
  className?: string;
}

export interface TimelineItemProps extends AntTimelineItemProps {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  timeline: {
    '&.ant-timeline': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
    },
    '& .ant-timeline-item-tail': {
      borderColor: theme.color.semantic.border.primary,
      borderWidth: theme.border.width.sm,
    },
    '& .ant-timeline-item-head': {
      backgroundColor: theme.color.semantic.brand.default.primary,
      borderColor: theme.color.semantic.brand.default.primary,
      borderRadius: theme.radius.full,
      color: theme.color.semantic.text.inverse,
      width: '10px',
      height: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& .anticon': {
        color: `${theme.color.semantic.text.inverse} !important`,
        fontSize: theme.typography.fontSize.sm,
      },
    },
    '& .ant-timeline-item-head-custom': {
      width: '16px',
      height: '16px',
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& .ant-timeline-item-content': {
      marginLeft: theme.spacing[5],
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
      top: '-5px',
    },
  },
  timelinePending: {
    '& .ant-timeline-item-pending .ant-timeline-item-content': {
      color: theme.color.semantic.text.secondary,
    },
  },
  // Mode-specific styling
  timelineLeft: {
    '&.ant-timeline.ant-timeline-left': {},
  },
  timelineRight: {
    '&.ant-timeline.ant-timeline-right': {},
  },
  timelineAlternate: {
    '&.ant-timeline.ant-timeline-alternate': {},
  },
  // Item color variants (dot colors)
  timelineItemBlue: {
    '& .ant-timeline-item-head': {
      backgroundColor: theme.color.semantic.brand.default.primary,
      borderColor: theme.color.semantic.brand.default.primary,
      color: theme.color.semantic.text.inverse,
      width: '10px',
      height: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& .anticon': {
        color: `${theme.color.semantic.text.inverse} !important`,
        fontSize: theme.typography.fontSize.sm,
      },
    },
    '& .ant-timeline-item-head-custom': {
      width: '16px',
      height: '16px',
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  timelineItemGray: {
    '& .ant-timeline-item-head': {
      backgroundColor: theme.color.semantic.background.neutral.soft,
      borderColor: theme.color.semantic.background.neutral.soft,
      color: theme.color.semantic.text.primary,
      width: '10px',
      height: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& .anticon': {
        color: `${theme.color.semantic.text.primary} !important`,
        fontSize: theme.typography.fontSize.sm,
      },
    },
    '& .ant-timeline-item-head-custom': {
      width: '16px',
      height: '16px',
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  timelineItemRed: {
    '& .ant-timeline-item-head': {
      backgroundColor: theme.color.semantic.brand.danger.primary,
      borderColor: theme.color.semantic.brand.danger.primary,
      color: theme.color.semantic.text.inverse,
      width: '10px',
      height: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& .anticon': {
        color: `${theme.color.semantic.text.inverse} !important`,
        fontSize: theme.typography.fontSize.sm,
      },
    },
    '& .ant-timeline-item-head-custom': {
      width: '16px',
      height: '16px',
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  timelineItemGreen: {
    '& .ant-timeline-item-head': {
      backgroundColor: theme.color.semantic.accent.green.strong,
      borderColor: theme.color.semantic.accent.green.strong,
      color: theme.color.semantic.text.inverse,
      width: '10px',
      height: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& .anticon': {
        color: `${theme.color.semantic.text.inverse} !important`,
        fontSize: theme.typography.fontSize.sm,
      },
    },
    '& .ant-timeline-item-head-custom': {
      width: '16px',
      height: '16px',
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
}));

const TimelineInner: React.FC<TimelineProps> = ({ mode = 'left', className = '', pending, ...rest }) => {
  const classes = useStyles();

  const getModeClass = () => {
    switch (mode) {
      case 'left':
        return classes.timelineLeft;
      case 'right':
        return classes.timelineRight;
      case 'alternate':
        return classes.timelineAlternate;
      default:
        return classes.timelineLeft;
    }
  };

  const combinedClassName = `${classes.timeline} ${getModeClass()} ${pending ? classes.timelinePending : ''} ${className}`.trim();

  return (
    <AntTimeline mode={mode} pending={pending} {...rest} className={combinedClassName} />
  );
};

// Wrapped Timeline.Item with color class support
const TimelineItem: React.FC<TimelineItemProps> = ({ color, className = '', ...rest }) => {
  const classes = useStyles();

  const getColorClass = () => {
    switch (color) {
      case 'blue':
        return classes.timelineItemBlue;
      case 'gray':
        return classes.timelineItemGray;
      case 'red':
        return classes.timelineItemRed;
      case 'green':
        return classes.timelineItemGreen;
      default:
        return '';
    }
  };

  const combinedClassName = `${getColorClass()} ${className}`.trim();

  return (
    <AntTimeline.Item color={color} {...rest} className={combinedClassName} />
  );
};

// Create Timeline with Item property
export const Timeline = Object.assign(TimelineInner, {
  Item: TimelineItem,
});

export default Timeline;
